/**
 * CV pricing-mapping editor logic (D116 / RS-7).
 *
 * WHY THIS EXISTS. The Admin panel can create a section type, and the pricer
 * then refuses it with a visible needs-pricing line ("no CV mapping for type
 * X") — by design, but the only fix was a code change and a redeploy, which
 * contradicts "business rules are admin-editable data, not code". This module
 * is the DOM-free half of the Admin panel's mapping editor: it builds the
 * editable grid, applies edits without clobbering fields it does not know
 * about, validates a row against the CATALOG'S OWN attributes (a Cabinet
 * Vision type NAME is never evidence of its configuration — owner ruling
 * D111), and enumerates which catalog families no sketch type can reach
 * (the RS-7 class, scoped here for the first time).
 *
 * Everything takes the RESOLVED rules objects (`rules.cvTypeMapping`,
 * `rules.cvSectionCatalog`), so admin overrides of either flow through.
 */

/** The catalog's section rows, whether handed the export object or the array. */
function sectionsOf(catalog) {
  if (Array.isArray(catalog)) return catalog;
  return Array.isArray(catalog?.sections) ? catalog.sections : [];
}

/** Distinct catalog classes, sorted. */
export function catalogClasses(catalog) {
  return [...new Set(sectionsOf(catalog).map((r) => String(r.class ?? '')).filter(Boolean))].sort();
}

/**
 * Distinct configs priced in one class, sorted. These are the only legal
 * values for a mapping row's config/doorConfig in that class: the pricer
 * never crosses configs or classes, so a pair absent here can never price.
 */
export function catalogConfigsFor(catalog, classId) {
  return [...new Set(sectionsOf(catalog)
    .filter((r) => r.class === classId)
    .map((r) => String(r.config ?? ''))
    .filter(Boolean))].sort();
}

/** Whether the catalog prices at least one row of `classId`|`config`. */
export function catalogHasPair(catalog, classId, config) {
  return sectionsOf(catalog).some((r) => r.class === classId && r.config === config);
}

/**
 * One editable grid row per section-type code — the UNION of the placeable
 * presets and the mapping rows, so both gaps show: a preset with no mapping
 * ("unmapped" — places but cannot price, the D116 defect) and a mapping with
 * no preset ("mapped-no-preset" — prices the moment a preset is added).
 */
export function mappingGrid(mapping, presets) {
  const rows = new Map((mapping?.rows ?? []).map((r) => [String(r.code ?? ''), r]));
  const seen = new Set();
  const grid = [];
  for (const p of presets ?? []) {
    const code = String(p.code ?? '');
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const row = rows.get(code) ?? null;
    grid.push({
      code,
      name: String(p.name ?? ''),
      hasPreset: true,
      row,
      status: !row ? 'unmapped' : row.zeroPrice === true ? 'zero-price' : 'mapped',
    });
  }
  for (const [code, row] of rows) {
    if (!code || seen.has(code)) continue;
    seen.add(code);
    grid.push({ code, name: '', hasPreset: false, row, status: 'mapped-no-preset' });
  }
  return grid;
}

/**
 * A new mapping object with one row edited. `...row` FIRST is load-bearing
 * (same drift rule as the type presets, 2026-07-27): fields this editor does
 * not surface — configByShoeStyle, configByDrawerCount, hamperAccessory,
 * interim — ride through untouched, so saving a class change can never strip
 * the shoe-style switch off SS. A patch value of `null` deletes that field;
 * `patch === null` deletes the whole row.
 */
export function applyMappingEdit(mapping, code, patch) {
  const rows = [...(mapping?.rows ?? [])];
  const at = rows.findIndex((r) => String(r.code ?? '') === code);
  if (patch === null) {
    if (at >= 0) rows.splice(at, 1);
    return { ...mapping, rows };
  }
  const base = at >= 0 ? rows[at] : { code };
  const next = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === undefined || value === '') delete next[key];
    else next[key] = value;
  }
  next.code = code;
  if (at >= 0) rows[at] = next;
  else rows.push(next);
  return { ...mapping, rows };
}

/**
 * Errors that make a row unsaveable, each naming the field. Empty = valid.
 * A zero-price row needs nothing else. Otherwise the base pair must exist in
 * the catalog (the pricer's exact-match-then-same-pair-fallback can never
 * leave the pair), and a doorConfig must form a priced pair in the SAME class
 * (the door delta is queried there — space-pricing.js, configDelta).
 */
export function validateMappingRow(row, catalog) {
  const errors = [];
  if (!row || typeof row !== 'object') return ['row missing'];
  if (row.zeroPrice === true) return errors;
  const classId = String(row.class ?? '');
  const config = String(row.config ?? '');
  if (!classId) errors.push(`${row.code}: class is required (or mark it zero-price)`);
  else if (!catalogClasses(catalog).includes(classId)) errors.push(`${row.code}: class ${classId} prices nothing in the catalog`);
  if (!config) errors.push(`${row.code}: config is required (or mark it zero-price)`);
  else if (classId && !catalogHasPair(catalog, classId, config)) errors.push(`${row.code}: ${classId}|${config} has no catalog rows`);
  if (row.doorConfig != null && classId && !catalogHasPair(catalog, classId, String(row.doorConfig))) {
    errors.push(`${row.code}: door config ${classId}|${row.doorConfig} has no catalog rows`);
  }
  if (row.cvHeightIn != null && !(Number(row.cvHeightIn) > 0)) errors.push(`${row.code}: CV height must be a positive number`);
  return errors;
}

/**
 * Catalog families (class|config pairs with priced rows) that NO mapping row
 * can reach — the RS-7 class, first scoped 2026-08-28. The reachability model
 * mirrors space-pricing.js's real lookup paths:
 *   - the row's base class|config;
 *   - FSS/SSS when `configByShoeStyle === true` (the switch is hardcoded to
 *     those two styles — space-pricing.js:335-337);
 *   - every `configByDrawerCount` value (:339-346);
 *   - the doorConfig pair, queried in the row's OWN class (:385-386);
 *   - config `P` for every class a row prices in (included-panel path, :143);
 *   - CS|CS, reached directly by connecting shelves (:614);
 *   - stackedClass|stackedConfig and stackedClass|stackedDoorConfig — a
 *     stacked unit prices as shelving-with-or-without-door (C1, :324-332),
 *     never as its own run-level config.
 * A family listed here cannot price no matter what a designer draws; the fix
 * is a mapping row (or a preset for an already-mapped code), which this
 * editor can now create.
 */
export function unreachableCatalogFamilies(mapping, catalog) {
  const families = new Map();
  for (const r of sectionsOf(catalog)) {
    const key = `${r.class}|${r.config}`;
    families.set(key, (families.get(key) ?? 0) + 1);
  }
  const reachable = new Set();
  const stacked = mapping?.stackedClass ?? 'ST';
  const classesInUse = new Set();
  for (const row of mapping?.rows ?? []) {
    if (row.zeroPrice === true) continue;
    const classId = String(row.class ?? '');
    if (!classId) continue;
    classesInUse.add(classId);
    const configs = new Set();
    if (row.config) configs.add(String(row.config));
    if (row.configByShoeStyle === true) { configs.add('FSS'); configs.add('SSS'); }
    for (const v of Object.values(row.configByDrawerCount ?? {})) configs.add(String(v));
    for (const config of configs) {
      reachable.add(`${classId}|${config}`);
    }
    if (row.doorConfig) reachable.add(`${classId}|${row.doorConfig}`);
  }
  // Stacked reachability models the pricer's C1 semantics (owner,
  // 2026-08-29: "stacked units are shelving sections … with or without
  // door"): a stacked unit prices as ST|<stackedConfig> or, with doors,
  // ST|<stackedDoorConfig> — never its own run-level config. So exactly
  // those two ST families are reachable, and reachable whenever any
  // mapped class exists to stack on. (The old model added ST|<config> for
  // every mapped config, which both over-claimed — ST|FS was never a real
  // lookup — and under-claimed: ST|ShDr, reached through doors, was
  // reported unreachable. RS-7 recounts 8 → 7 on this correction.)
  if (classesInUse.size) {
    reachable.add(`${stacked}|${mapping?.stackedConfig ?? 'ShOp'}`);
    reachable.add(`${stacked}|${mapping?.stackedDoorConfig ?? 'ShDr'}`);
  }
  for (const classId of classesInUse) reachable.add(`${classId}|P`);
  reachable.add('CS|CS');
  return [...families.entries()]
    .filter(([family]) => !reachable.has(family))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([family, rows]) => ({ family, rows }));
}
